import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { ModalUI } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { AdvertiserSelector } from '@/components/common/AdvertiserSelector';
import { CampaignPlanSelector } from '@/components/common/CampaignPlanSelector';
import { MediaChannelSelector } from '@/components/ioSettings/InsertionOrderForm/MediaChannelSelection/MediaChannelSelector/MediaChannelSelector';
import { useModals, useNavigate } from '@/router';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';

import './modal.scss';

import type { MediaAdvertiserUserInterface } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.schema';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';

type State = {
  advertiser?: MediaAdvertiserUserInterface;
  campaignPlan?: CampaignPlan;
  mediaChannel: CampaignFamilyEnum;
};

type SelectorEntityKey = 'advertiser' | 'campaignPlan';

export default function InsertionOrderModal() {
  const { formatMessage } = useIntl();
  const modals = useModals();
  const navigate = useNavigate();
  const location = useLocation();
  const modalRef = useRef<any>(null);
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const { setHasInsertionOrders } = useCampaignStore() as CampaignsStore;

  const prefilledCampaignPlan = (location.state as { campaignPlan?: CampaignPlan } | null)?.campaignPlan;

  const [{ advertiser, campaignPlan, mediaChannel }, setState] = useState<State>({
    campaignPlan: prefilledCampaignPlan,
    mediaChannel: CampaignFamilyEnum.standard
  });

  const userJSON = user.toJSON();

  const mediaChannelOptions = [
    CampaignFamilyEnum.standard,
    ...(checkUserResourcePermissions('campaignType', userJSON, userRoles) ? [CampaignFamilyEnum.dooh] : []),
    ...(checkUserResourcePermissions('ctvDevicePlatform', userJSON, userRoles) ? [CampaignFamilyEnum.ctv] : [])
  ];

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  const handleEntitySelectorChange = (
    entityType: SelectorEntityKey,
    entitySelected: MediaAdvertiserUserInterface | CampaignPlan
  ) => {
    setState(prevState => ({
      ...prevState,
      [entityType]: entitySelected,
      ...(entityType === 'advertiser' && { campaignPlan: undefined })
    }));
  };

  const handleMediaChannelChange = (newChannel: CampaignFamilyEnum) => {
    setState(prevState => ({ ...prevState, mediaChannel: newChannel }));
  };

  const handleClose = () =>
    modals.close({
      at: {
        pathname: '/campaigns',
        search: location.search
      }
    });

  const handleFinish = () => {
    setHasInsertionOrders(true);
    navigate('/ioSettings/create', {
      state: {
        campaignPlan,
        mediaChannel,
        originPath: location.pathname + location.search
      }
    });
  };

  const steps = [
    {
      component: (
        <div className="insertionOrderModal">
          <div className="insertionOrderModal__row">
            <div className="entitySelectorsGrid">
              <div>
                <AdvertiserSelector
                  id={'insertionOrderModalAdvertiserSelector'}
                  onChange={adv => handleEntitySelectorChange('advertiser', adv)}
                />
              </div>
              <CampaignPlanSelector
                advertiserId={advertiser?._id}
                disabled={!advertiser}
                id={'insertionOrderModalCampaignPlanSelector'}
                key={advertiser?._id}
                onChange={cp => handleEntitySelectorChange('campaignPlan', cp)}
              />
            </div>
          </div>
        </div>
      ),
      description: formatMessage({
        defaultMessage: 'Define the advertiser and campaign plan.',
        id: 'campaignPlans.modal.step.defineAdvertiserAndCampaignPlan'
      }),
      title: formatMessage({
        defaultMessage: 'Campaign Setup',
        id: 'campaignPlans.modal.step.campaignSetup'
      })
    },
    {
      component: (
        <MediaChannelSelector
          id="insertionOrderModal-mediaChannelSelector"
          onChange={handleMediaChannelChange}
          options={mediaChannelOptions}
          showTitle={false}
          subTitle={formatMessage({
            defaultMessage: 'Ensure a successful IO configuration by prioritizing the Media Channel selection.',
            id: 'mediaChannelSelection.creation.subtitle'
          })}
          title={formatMessage({
            defaultMessage: "Let's start by selecting the Media Channel for the Insertion Order",
            id: 'mediaChannelSelection.creation.title'
          })}
          value={mediaChannel}
        />
      ),
      description: formatMessage({
        defaultMessage: 'Choose the advertising environment for your Insertion Order.',
        id: 'campaignPlans.modal.step.selectMediaChannelDescription'
      }),
      title: formatMessage({
        defaultMessage: 'Media Channel',
        id: 'campaignPlans.modal.step.selectMediaChannel'
      })
    }
  ];

  return (
    <ModalUI
      closeModalCallback={handleClose}
      disabledNextButton={!campaignPlan}
      finishButton={{
        icon: 'fa-solid fa-arrow-right',
        iconPosition: 'right',
        id: 'ioSettingsFinishBtn',
        onClick: handleFinish,
        text: formatMessage({
          defaultMessage: 'Go to Insertion Order settings',
          id: 'mediaChannelSelection.creation.button'
        })
      }}
      ref={modalRef}
      steps={prefilledCampaignPlan ? [steps[1]] : steps}
      title=""
      width="805px"
    />
  );
}
